import JSZip from 'jszip';

export type ParsedSnapshotZipEntry = {
  path: string;
  u8: Uint8Array;
};

/** Parse snapshot zip entries off the main thread when a Worker is available. */
export async function parseSnapshotZipBlob(blob: Blob): Promise<ParsedSnapshotZipEntry[]> {
  if (typeof Worker !== 'undefined') {
    try {
      return await parseSnapshotZipBlobInWorker(blob);
    } catch {
      /* fall through to main-thread parse */
    }
  }
  return parseSnapshotZipBlobMainThread(blob);
}

async function parseSnapshotZipBlobMainThread(blob: Blob): Promise<ParsedSnapshotZipEntry[]> {
  const zip = await JSZip.loadAsync(blob);
  const entries: ParsedSnapshotZipEntry[] = [];
  for (const path of Object.keys(zip.files)) {
    const entry = zip.files[path];
    if (!entry || entry.dir) continue;
    const u8 = await entry.async('uint8array');
    entries.push({ path, u8 });
  }
  return entries;
}

function parseSnapshotZipBlobInWorker(blob: Blob): Promise<ParsedSnapshotZipEntry[]> {
  return new Promise((resolve, reject) => {
    const workerSource = `
      importScripts('https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js');
      self.onmessage = async (event) => {
        try {
          const zip = await JSZip.loadAsync(event.data);
          const entries = [];
          for (const path of Object.keys(zip.files)) {
            const entry = zip.files[path];
            if (!entry || entry.dir) continue;
            const u8 = await entry.async('uint8array');
            entries.push({ path, u8: u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) });
          }
          self.postMessage({ ok: true, entries }, entries.map((e) => e.u8));
        } catch (err) {
          self.postMessage({ ok: false, error: String(err) });
        }
      };
    `;
    const workerUrl = URL.createObjectURL(
      new Blob([workerSource], { type: 'application/javascript' }),
    );
    const worker = new Worker(workerUrl);
    URL.revokeObjectURL(workerUrl);
    worker.onmessage = (event: MessageEvent<{ ok: boolean; entries?: ParsedSnapshotZipEntry[]; error?: string }>) => {
      worker.terminate();
      if (!event.data.ok || !event.data.entries) {
        reject(new Error(event.data.error ?? 'Worker zip parse failed'));
        return;
      }
      resolve(
        event.data.entries.map((entry) => ({
          path: entry.path,
          u8: new Uint8Array(entry.u8 as unknown as ArrayBuffer),
        })),
      );
    };
    worker.onerror = (err) => {
      worker.terminate();
      reject(err);
    };
    worker.postMessage(blob);
  });
}
